import { Button, Grid2, Stack } from '@mui/material';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { PresignedAudioAttachment } from '@components/Attachments';
import Icon from '@components/Icon/Icon';

import { COUGH_SCAN } from '@constants/routes';
import {
  COUGH_LOW_DISEASE_LIKELIHOOD,
  COUGH_MID_DISEASE_LIKELIHOOD,
} from '@constants/scan';

import { useScanResults } from '@hooks/useScanResults';
import { useSession } from '@hooks/useSession';

import type { ScanResult } from '@models/scan';

import { createDateAsUTC } from '@utils/date';

import { useGetS3AssetRecordLazyQuery } from 'generated/file.generated';
import { ReferenceRangeRiskLevel } from 'generated/types';

import Content from '../Common/CardContent';
import LikelihoodCard from '../Common/LikelihoodCard';

const messages = defineMessages({
  infectionRisk: {
    id: 'coughScanCard.infectionRisk.title',
    defaultMessage: 'Risk of Infection:',
  },
  highRisk: {
    id: 'coughScanCard.infectionRisk.high',
    defaultMessage: 'High',
  },
  lowRisk: {
    id: 'coughScanCard.infectionRisk.low',
    defaultMessage: 'Low',
  },
  coughPattern: {
    id: 'coughScanCard.coughPattern.title',
    defaultMessage: 'Pattern: {pattern}',
  },
  clear: {
    id: 'coughScanCard.coughPattern.clear',
    defaultMessage: 'Clear',
  },
  obstructive: {
    id: 'coughScanCard.coughPattern.obstructive',
    defaultMessage: 'Obstructive',
  },
  viewResults: {
    id: 'coughScanCard.viewResults',
    defaultMessage: 'View Detailed Results',
  },
  noDiseases: {
    id: 'coughScanCard.noDiseases',
    defaultMessage: 'No disease likelihoods',
  },
  likelihood: {
    id: 'coughScanCard.likelihood',
    defaultMessage:
      '{isLikely, select, true {Likely} other {Unlikely}} - {percentage}%',
  },
  audio: {
    id: 'coughScanCard.audio',
    defaultMessage: 'Cough Audio',
  },
});

interface CoughScanContentProps {
  data: ScanResult;
}

export default function CoughScanContent({ data }: CoughScanContentProps) {
  const { formatMessage } = useIntl();
  const { is_sharing, scan_type } = useScanResults();
  const { attachment, result_id } = data;
  const {
    state: { activeProfile },
  } = useSession();
  const [getS3Asset] = useGetS3AssetRecordLazyQuery();
  const date = createDateAsUTC(new Date(data.date));

  const {
    disease_likelihoods: likelihoods,
    high_infection_risk,
    obstructive_cough_pattern,
  } = data.cough_results ?? {};
  const { high, moderate, normal } = ReferenceRangeRiskLevel;

  const disease_likelihoods = likelihoods?.filter(
    ({ likelihood }) => (likelihood ?? 0) >= COUGH_LOW_DISEASE_LIKELIHOOD
  );

  const fetchScanAttachment = async (attachmentId: string): Promise<string> => {
    const { data } = await getS3Asset({
      variables: {
        input: {
          // Should this be passed as a prop instead?
          patient_id: activeProfile?.patient_id ?? '',
          record_id: attachmentId,
        },
      },
    });
    if (!data?.getS3AssetRecord.presignedGetUrl) {
      throw new Error('Failed to get URL');
    }
    return data.getS3AssetRecord.presignedGetUrl;
  };

  const actions = (
    <Stack direction="row" justifyContent="space-between" width="100%">
      {attachment?.id ? (
        <PresignedAudioAttachment
          fetch={() => fetchScanAttachment(attachment.id ?? '')}
        />
      ) : (
        <span />
      )}
      {!is_sharing && (
        <Button
          endIcon={<Icon name="arrow-right" type="fas" />}
          onClick={() => navigate(`${COUGH_SCAN}${result_id}`)}
          size="small"
          sx={{
            textTransform: 'capitalize',
            fontSize: '14px',
          }}
          variant="text"
        >
          {formatMessage(messages.viewResults)}
        </Button>
      )}
    </Stack>
  );

  return (
    <Content
      actions={actions}
      date={date}
      header={formatMessage(messages.coughPattern, {
        pattern: formatMessage(
          obstructive_cough_pattern ? messages.obstructive : messages.clear
        ),
      })}
      title={{
        label: formatMessage(messages.infectionRisk),
        risk: high_infection_risk
          ? ReferenceRangeRiskLevel.high
          : ReferenceRangeRiskLevel.normal,
        value: formatMessage(
          high_infection_risk ? messages.highRisk : messages.lowRisk
        ),
      }}
    >
      <Grid2 container gap={1}>
        {disease_likelihoods?.length ? (
          disease_likelihoods.map(
            ({ disease_code, disease_name, likelihood }) => {
              /**
               * 0-39%: Unlikely (green)
               * 40-79%: Likely (orange)
               * 80-100%: Likely (red)
               */
              const likelihoodPercent = likelihood ?? 0;
              const level =
                likelihoodPercent >= COUGH_MID_DISEASE_LIKELIHOOD
                  ? high
                  : likelihoodPercent >= COUGH_LOW_DISEASE_LIKELIHOOD
                    ? moderate
                    : normal;
              return (
                <Grid2
                  key={`${result_id}:${scan_type}-scan-card-${disease_code}`}
                >
                  <LikelihoodCard
                    level={level}
                    subtitle={formatMessage(messages.likelihood, {
                      isLikely:
                        likelihoodPercent >= COUGH_LOW_DISEASE_LIKELIHOOD,
                      percentage: likelihoodPercent,
                    })}
                    title={disease_name}
                  />
                </Grid2>
              );
            }
          )
        ) : (
          <Grid2>
            <LikelihoodCard
              level={ReferenceRangeRiskLevel.normal}
              title={formatMessage(messages.noDiseases)}
            />
          </Grid2>
        )}
      </Grid2>
    </Content>
  );
}
